import { useEffect, useState } from 'react';
import { useStore } from '../../context';

export const useText = () => {
  const file = useStore(({ file: f }) => f.getFile());
  const textContent = useStore(({ file: f }) => f.getTextContent());
  /* Local State */
  const [text, setText] = useState<string>('');
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [linesPerPage] = useState(50);

  /* Effects */
  useEffect(() => {
    readFile();
  }, [file, linesPerPage]);

  // Use textContent from store if available, otherwise read from file
  useEffect(() => {
    if (textContent) {
      // Ensure consistent text processing by normalizing line endings
      const normalizedText = textContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      setText(normalizedText);
      setNumPages(Math.ceil(normalizedText.split('\n').length / linesPerPage));
    }
  }, [textContent, linesPerPage]);

  /**
   * Handlers
   **/
  const readFile = async () => {
    if (!file) {
      console.error("File is null. Cannot read file.");
      return;
    }

    const fileText = await file.text();
    setText(fileText);
    setNumPages(Math.ceil(fileText.split('\n').length / linesPerPage));
  };

  const getPageText = () => {
    const lines = text.split('\n');
    const start = (pageNumber - 1) * linesPerPage;
    const end = start + linesPerPage;
    // Clean the text from page for consistent comparison
    const pageText = lines.slice(start, end).join('\n');

    return pageText;
  };

  // Returns the global offset of the start of the current page
  const getPageOffset = () => {
    const lines = text.split('\n');
    const start = (pageNumber - 1) * linesPerPage;

    // Calculate offset by reconstructing the text up to the start of this page
    let offset = 0;
    for (let i = 0; i < start && i < lines.length; i++) {
      offset += lines[i].length + 1; // +1 for the '\n'
    }

    // Verify the offset calculation by checking if it matches the actual text
    const reconstructedText = lines.slice(0, start).join('\n');

    // Additional verification: check if the offset calculation is correct
    if (reconstructedText.length !== offset) {
      console.warn('[PageOffset] WARNING: Offset calculation mismatch!', {
        reconstructedLength: reconstructedText.length,
        calculatedOffset: offset,
        difference: reconstructedText.length - offset
      });
    }

    return offset;
  };

  const getFullText = () => text;

  const previousPage = () => {
    setPageNumber((prevPageNumber) => Math.max(prevPageNumber - 1, 1));
  };

  const nextPage = () => {
    setPageNumber((prevPageNumber) => Math.min(prevPageNumber + 1, numPages));
  };

  return {
    pageNumber,
    numPages,
    getPageText,
    getPageOffset,
    getFullText,
    previousPage,
    nextPage,
  };
};
